// Manager Sub-mode C touch point preparation — DESIGN §5.6.2, §10.1.2 #3.
//
// The manager picks an upcoming touch point and Maeve produces a structured
// 2-3-pointer briefing (topic + reasoning + optional suggestedPhrasing).
// Multi-turn: the manager can ask follow-up questions; the assistant's
// replies render as conversational text below the pointer list. Pointers
// are refreshed from each response so a manager asking "give me different
// pointers" sees the new set replace the old.
//
// Server is stateless; history lives in component state and resets on
// touch point switch (the briefing is scoped to a specific touch point — mixing
// the conversation across touch points would confuse the model and the
// manager).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { TouchpointSummary } from '@besideu/shared/cases';
import type {
  TouchpointPrepPointer,
  TouchpointPrepResponse,
  GuidanceTurn,
} from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from './ui/Button.tsx';
import { ChatBubble } from './chat/ChatBubble.tsx';

const TOUCHPOINT_LABELS: Record<TouchpointSummary['type'], string> = {
  Day_3: 'Day 3',
  Week_1: 'Week 1',
  Pre_Return: 'Pre-return',
  Week_1_Back: 'Week 1 back',
  Month_1: 'Month 1',
  Month_3: 'Month 3',
  // PO Spec §3 (2026-06-21) — 12-month series expansion.
  Month_6: 'Month 6',
  Month_9: 'Month 9',
  Month_12: 'Month 12',
  Anniversary: 'Anniversary',
};

interface TouchpointPrepApiResponse extends TouchpointPrepResponse {
  promptVersionId: string;
}

/**
 * Can Maeve brief against this touch point? Only forward-looking +
 * still-actionable ones: a Completed / Done_Informally / Suppressed / Skipped
 * touch point is in the past and there is nothing useful to brief. Overdue is
 * included — the manager may be running late and still want the briefing.
 *
 * Exported because it is now a CONTRACT between two components, not an
 * implementation detail: `CaseDetailView` offers a "Prepare with Maeve" control
 * per row, and that control must not appear on a row this card would refuse to
 * select (it silently did before this was shared).
 */
export function isBriefableTouchpoint(t: TouchpointSummary): boolean {
  return t.status === 'Pending' || t.status === 'Overdue';
}

export function ManagerMaeveTouchpointPrep({
  caseId,
  touchpoints,
  preselectTouchpointId,
  autoStartTouchpointId,
  onAnnounce,
}: {
  caseId: string;
  touchpoints: TouchpointSummary[];
  // Journey 04 focused check-in page: preselect a specific touch point and
  // draft its briefing on mount, hiding the picker. Omitted on the case-detail
  // page, where the manager picks from the list (unchanged behaviour).
  preselectTouchpointId?: string | undefined;
  /**
   * Case-detail deep link (PO 2026-08-06): the timeline's per-row "Prepare for
   * this touch point with Maeve" control names a specific touch point, so it
   * has to actually select that one. Unlike `preselectTouchpointId` this KEEPS
   * the picker — the manager landed here from one row but can still switch.
   *
   * Driven by parent state, deliberately NOT a URL search param: this starts a
   * briefing, which is a billed Vertex call and an audited `maeve_interactions`
   * row, and a `?prep=<id>` in the address bar would re-fire it on every
   * refresh, back-navigation or shared link.
   */
  autoStartTouchpointId?: string | null | undefined;
  /**
   * When provided, this card's own sr-only `aria-live` region is skipped and
   * the latest briefing/reply text is reported here instead — a live region
   * inside a `hidden` ancestor (e.g. an inactive tab panel) never announces,
   * silently dropping the notification, so a caller that can be tabbed away
   * from (`ManagerCaseDetail`) lifts a single shared, always-visible live
   * region to the page level and feeds it from here.
   */
  onAnnounce?: (message: string) => void;
}) {
  // See `isBriefableTouchpoint` for what "reachable" means and why the
  // predicate is shared rather than inlined here.
  const reachable = useMemo(() => touchpoints.filter(isBriefableTouchpoint), [touchpoints]);

  const [selectedId, setSelectedId] = useState<string | null>(preselectTouchpointId ?? null);
  const [history, setHistory] = useState<GuidanceTurn[]>([]);
  const [pointers, setPointers] = useState<TouchpointPrepPointer[] | null>(null);
  const [followUp, setFollowUp] = useState('');

  const selected = useMemo(
    () => reachable.find((ci) => ci.id === selectedId) ?? null,
    [reachable, selectedId],
  );

  // Screen-reader announcement string — re-derives from `pointers` and the
  // last assistant turn so AT users hear when a new briefing arrives or
  // when Maeve answers a follow-up. Mirrors the `aria-live="polite"` SR-only
  // pattern in `ManagerMaeveGuidance` / `HrMaeveChat`; without it AT users
  // get no signal that the briefing has landed (WCAG 2.1 AA 4.1.3).
  const announcement = useMemo(() => {
    const lastAssistant = [...history].reverse().find((t) => t.role === 'assistant');
    // Prefer the follow-up message text when present — that's the model
    // directly addressing the manager. Fall back to the pointer-count
    // summary when only pointers were emitted (initial briefing turn,
    // where `assistant.text` is the synthesised placeholder).
    if (lastAssistant && lastAssistant.text !== 'See the briefing pointers above.') {
      return `Maeve replied: ${lastAssistant.text}`;
    }
    if (pointers) {
      const topics = pointers.map((p) => p.topic).join('; ');
      return `Maeve has prepared ${pointers.length} briefing pointers: ${topics}`;
    }
    return '';
  }, [history, pointers]);

  // Deliberately omits `onAnnounce` from deps — callers rarely memoise it,
  // and re-firing this only when `announcement` actually changes (not on
  // every parent render) is the intended behaviour.
  useEffect(() => {
    if (onAnnounce && announcement) onAnnounce(announcement);
  }, [announcement]);

  function resetSession(nextId: string | null) {
    setSelectedId(nextId);
    setHistory([]);
    setPointers(null);
    setFollowUp('');
    // Clear any stale error from a previous touch point so the new briefing
    // starts with a clean panel — otherwise the prior touch point's error
    // card stays mounted alongside the new "preparing…" state.
    ask.reset();
  }

  // `history` is threaded into the mutation as an explicit parameter rather
  // than closed over from component state. The reason: `resetSession +
  // startBriefing` fire back-to-back on a touch point switch, and React batches
  // `setHistory([])` so the closure would still see the prior touch point's
  // history at the moment `ask.mutate` is called — sending another
  // touch point's conversation as context for a fresh briefing. Threading the
  // history explicitly closes that race.
  const ask = useMutation({
    mutationFn: async ({
      touchpointId,
      text,
      history: priorHistory,
    }: {
      touchpointId: string;
      text: string;
      history: GuidanceTurn[];
    }): Promise<{
      user: GuidanceTurn;
      assistant: GuidanceTurn;
      pointers: TouchpointPrepPointer[];
    }> => {
      const userTurn: GuidanceTurn = { role: 'user', text };
      const messages = [...priorHistory, userTurn];
      const resp = await api.post<TouchpointPrepApiResponse>(
        `/api/cases/${caseId}/maeve/touchpoint-prep`,
        { touchpointId, messages },
      );
      // Assistant turn for the transcript — use the model's `message` field
      // when present (follow-up turns), otherwise synthesise a placeholder
      // that points at the pointer list (initial briefing turn).
      const assistantText = resp.message ?? 'See the briefing pointers above.';
      const assistantTurn: GuidanceTurn = { role: 'assistant', text: assistantText };
      return { user: userTurn, assistant: assistantTurn, pointers: resp.pointers };
    },
    onSuccess: ({ user, assistant, pointers: newPointers }) => {
      setHistory((prev) => [...prev, user, assistant]);
      setPointers(newPointers);
      setFollowUp('');
    },
  });

  function startBriefing(touchpointId: string) {
    ask.mutate({
      touchpointId,
      // The opening prompt the model sees as the user's first message —
      // generic enough that the prompt's sub-mode-detection routes it to
      // touch point prep, given the sub-mode tag in the system instruction.
      text: 'Please prepare me for this touch point.',
      // Initial briefing has no history — pass empty explicitly because
      // the calling site has just reset `history` state and the React
      // batch may not have flushed yet (see the comment on `ask`).
      history: [],
    });
  }

  // Two auto-start entry points, one mechanism:
  //   * Journey 04's focused check-in page, which preselects on mount.
  //   * The case-detail timeline's per-row "Prepare with Maeve" control, which
  //     can fire repeatedly and for a DIFFERENT touch point each time.
  //
  // The ref holds the last id auto-started rather than a fire-once boolean, so
  // a second press on another row starts that row's briefing while a second
  // press on the SAME row (or any unrelated re-render) does not re-bill a
  // duplicate Vertex call. `resetSession` clears the prior touch point's
  // transcript so the new briefing doesn't inherit it.
  const autoStartedId = useRef<string | null>(null);
  const requestedId = preselectTouchpointId ?? autoStartTouchpointId ?? null;
  useEffect(() => {
    if (!requestedId || autoStartedId.current === requestedId) return;
    autoStartedId.current = requestedId;
    if (selectedId !== requestedId) resetSession(requestedId);
    startBriefing(requestedId);
    // Depends only on the requested id — fires exactly once per distinct value.
    // (`ask.mutate` inside `startBriefing` is stable across renders.)
  }, [requestedId]);

  function submitFollowUp() {
    if (!selected) return;
    const trimmed = followUp.trim();
    if (trimmed.length === 0 || ask.isPending) return;
    ask.mutate({ touchpointId: selected.id, text: trimmed, history });
  }

  if (reachable.length === 0) {
    return null;
  }

  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <header>
        <h2 className="text-base font-semibold">What Maeve suggests</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {preselectTouchpointId
            ? '2-3 conversation pointers for this check-in. You can ask follow-up questions. Everything Maeve says is advisory — the conversation is yours to have your way.'
            : 'Pick an upcoming touch point; Maeve briefs you with 2-3 conversation pointers tied to the support pathway stage. You can ask follow-up questions — the pointers refresh as the conversation moves. Everything Maeve says is advisory.'}
        </p>
      </header>

      {preselectTouchpointId ? null : (
        <div className="flex flex-wrap gap-2">
          {reachable.map((ci) => {
            const isSelected = ci.id === selectedId;
            return (
              <Button
                key={ci.id}
                type="button"
                variant={isSelected ? 'primary' : 'secondary'}
                className="py-1.5 text-sm"
                onClick={() => {
                  if (isSelected) return;
                  resetSession(ci.id);
                  startBriefing(ci.id);
                }}
                disabled={ask.isPending}
              >
                {TOUCHPOINT_LABELS[ci.type]} · {ci.scheduledFor}
                {ci.isOverdue ? ' (overdue)' : ''}
              </Button>
            );
          })}
        </div>
      )}

      {selected ? (
        <>
          {ask.isPending && pointers === null ? (
            // `role` dropped when `onAnnounce` is provided — same reasoning
            // as `ManagerMaeveFirstResponse`'s drafting-status paragraph:
            // this text's own implicit live region would otherwise
            // double-announce alongside the parent's shared one once
            // `onAnnounce` later fires with the briefing/reply text.
            <p className="text-sm text-muted-foreground" role={onAnnounce ? undefined : 'status'}>
              Maeve is preparing the briefing…
            </p>
          ) : null}

          {pointers ? <PointerList pointers={pointers} /> : null}

          {/* Screen-reader-only live region for the latest Maeve output.
              `polite` rather than `assertive` because a Maeve briefing is
              not interruption-worthy — the manager has stopped to wait
              for the response, so a polite announcement won't be missed.
              Mirrors the pattern in `ManagerMaeveGuidance` and
              `HrMaeveChat`. Skipped when `onAnnounce` is provided — see the
              prop doc above. */}
          {onAnnounce ? null : (
            <div aria-live="polite" className="sr-only">
              {announcement}
            </div>
          )}

          {history.length > 0 ? <ChatTranscript history={history.slice(2)} /> : null}

          {ask.error ? <PrepError error={ask.error} onRetry={() => ask.reset()} /> : null}

          {pointers ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitFollowUp();
              }}
              className="space-y-2"
            >
              <label className="block">
                <span className="sr-only">Follow-up question</span>
                <textarea
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      submitFollowUp();
                    }
                  }}
                  placeholder="Why this topic? What if they don't want to talk?"
                  className="block w-full resize-y rounded-md border border-border bg-background p-3 text-base"
                  rows={2}
                  maxLength={4000}
                  disabled={ask.isPending}
                />
              </label>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  loading={ask.isPending}
                  disabled={followUp.trim().length === 0 || ask.isPending}
                  className="py-2 text-sm"
                >
                  Ask follow-up
                </Button>
              </div>
            </form>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function PointerList({ pointers }: { pointers: TouchpointPrepPointer[] }) {
  return (
    <ol className="space-y-3" aria-label="Briefing pointers">
      {pointers.map((p, idx) => (
        <li key={idx} className="rounded-md border border-primary/20 bg-primary/5 p-4 text-sm">
          <p className="font-medium">
            {idx + 1}. {p.topic}
          </p>
          <p className="mt-1 text-muted-foreground">{p.reasoning}</p>
          {p.suggestedPhrasing ? (
            <p className="mt-2 border-l-2 border-primary/30 pl-3 text-muted-foreground italic">
              &ldquo;{p.suggestedPhrasing}&rdquo;{' '}
              <span className="text-xs">(example — adapt; don&rsquo;t read it out)</span>
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function ChatTranscript({ history }: { history: GuidanceTurn[] }) {
  if (history.length === 0) return null;
  return (
    <ol className="space-y-3" aria-label="Follow-up conversation with Maeve">
      {history.map((turn, idx) => (
        <li key={idx}>
          <ChatBubble role={turn.role}>{turn.text}</ChatBubble>
        </li>
      ))}
    </ol>
  );
}

function PrepError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { headline, detail } = explainError(error);
  return (
    <div
      role="status"
      className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm"
    >
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      <Button variant="secondary" onClick={onRetry} className="py-1.5 text-sm">
        Dismiss
      </Button>
    </div>
  );
}

function explainError(error: unknown): { headline: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.message === 'maeve_unavailable') {
      return {
        headline: "Maeve isn't available right now",
        detail:
          'A besideU administrator needs to enable the Manager prompt before Maeve can prepare touch point briefings. Please contact your HR team.',
      };
    }
    if (error.status === 502 && error.message === 'maeve_upstream_error') {
      return {
        headline: "Couldn't reach Maeve",
        detail:
          'The briefing service is temporarily unavailable. Your request wasn’t sent — please try again in a moment.',
      };
    }
  }
  return {
    headline: 'Something went wrong',
    detail: 'Please try again, or refresh the page if the problem persists.',
  };
}
